import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { catchError, finalize, switchMap, throwError } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../core/enums/CartItemTypes';
import { ICinevilleCategoryChangeModalData } from '../../core/interfaces/ICinevilleCategoryChangeModalData';
import { CartService } from '../../core/services/CartService';
import { CartStore } from '../../core/stores/CartStore';
import { AlertModalComponent } from '../../../../shared/components/alert-modal/alert-modal.component';
import { CinevilleUtils } from '../../core/utils/CinevilleUtils';

@Component({
  selector: 'app-cineville-partner-code-modal',
  standalone: true,
  imports: [
    ModalComponent,
    FormControlComponent,
    LoaderComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe
  ],
  templateUrl: './cineville-partner-code-modal.component.html',
  styleUrl: './cineville-partner-code-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CinevillePartnerCodeModalComponent extends BaseModalComponent<ICinevilleCategoryChangeModalData> {

  @ViewChild('codeInput') private codeInput: ElementRef<HTMLInputElement>;
  @ViewChild('confirmBtn') private confirmBtn: ElementRef<HTMLButtonElement>;

  @Output() categoryChangeCompleted = new EventEmitter<void>();
  @Output() categoryChangeCancelled = new EventEmitter<void>();

  private readonly _cartService = inject(CartService);
  private readonly _cartStore = inject(CartStore);

  public readonly isLoading = signal(false);
  public code = '';
  private _categoryChangeSucceeded = false;

  public override open(val?: ICinevilleCategoryChangeModalData): void {
    this._categoryChangeSucceeded = false;
    this.code = '';
    this.isLoading.set(false);
    super.open(val);
    setTimeout(() => this.codeInput?.nativeElement?.focus(), 0);
  }

  public override close(): void {
    if (this.data && !this._categoryChangeSucceeded) {
      this.categoryChangeCancelled.emit();
    }
    const succeeded = this._categoryChangeSucceeded;
    this._categoryChangeSucceeded = false;
    super.close();
    if (succeeded) {
      this.categoryChangeCompleted.emit();
    }
  }

  public confirmCategoryChange(): void {
    const code = this.code?.trim();
    const data = this.data;
    if (!code || !data) {
      return;
    }

    const ticket = data.pendingTicket;
    const normalized = code.toLowerCase();

    const duplicateElsewhere = this._cartStore.cartItemsTickets().some(
      t =>
        t.itemType === CartItemTypes.Ticket &&
        Boolean(t.partnerCode?.trim()) &&
        t.partnerCode?.trim().toLowerCase() === normalized &&
        !(t.itemId === ticket.itemId && t.position === ticket.position)
    );

    if (duplicateElsewhere) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'This partner code is already used for another ticket in the cart.'.trans(
          'cart/cineville-partner-code-modal/duplicate-code.Message'
        )
      );
      return;
    }

    const btn = this.confirmBtn?.nativeElement;
    btn?.classList.add('disabled');
    this.isLoading.set(true);

    this._cartService
      .updateCartItem([data.pendingTicket])
      .pipe(
        switchMap(() =>
          this._cartService.attachCinevillePartnerCode(ticket.itemId, ticket.position, code).pipe(
            catchError(err => {
              const rollback = {
                ...data.pendingTicket,
                priceCategoryId: data.revertPriceCategoryId,
                packageId: null,
                packageProductIds: []
              };
              return this._cartService.updateCartItem([rollback]).pipe(
                switchMap(() => throwError(() => err))
              );
            })
          )
        ),
        finalize(() => {
          btn?.classList.remove('disabled');
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: () => {
          this._categoryChangeSucceeded = true;
          this.code = '';
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          if (error.message === 'CinevilleCheckInTooEarly') {
            AlertModalComponent.openAlertModal(
              'Info'.trans('notifications/info.Title'),
              CinevilleUtils.getCinevilleCheckInTooEarlyMessage(),
              this.destroyRef
            );
            return;
          }

          const cinevilleMessage = CinevilleUtils.resolveCinevilleErrorMessage(error);
          if (cinevilleMessage) {
            AlertModalComponent.openAlertModal(
              'Fehler'.trans('notifications/error.Title'),
              cinevilleMessage,
              this.destroyRef
            );
            return;
          }

          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`vouchers/external/cineville/notifications/${error.message}.Message`)
          );
        }
      });
  }
}
